
import React, { useState } from "react";
import {
  Box, Flex, Heading, Badge, Button, Icon, Center,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  useDisclosure, useToast
} from "@chakra-ui/react";
import { FiCheckCircle, FiEdit2, FiTrash2 } from "react-icons/fi";
import UpdateActivationModal from "../modals/UpdateActivationModal";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import EditActivationModal from "../modals/EditActivationModal";

export default function ActivationDetails({
  scoreBreakdown,
  onViewActivation,
  onValidateAct,
  isValidatingAct,
  selectedStoreDetails,
  isLoadingActivationImage,
  generatePresignedUrls,
  onRefresh,
}) {
  const activation = scoreBreakdown?.activation;
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingIdx, setDeletingIdx] = useState(null); 

  const toast = useToast();
  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const handleUpdateClick = (detail) => {
    setSelectedDetail(detail);
    onUpdateOpen();
  };

  const handleUpdateSave = async (detail, formData) => {
    setIsUpdating(true);
    try {
      const runId = scoreBreakdown?.availability?.products?.[0]?.runId;
      const storeId = scoreBreakdown?.store?.storeId;
      const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
      const auditDate = selectedStoreDetails?.auditedDate;

      await apiClient.put(`${API_CONFIG.ENDPOINTS.STORE_REPORT.ACTIVATION.UPDATE}${detail.product?.productId}`, {
        runId: Number(runId),
        storeId: Number(storeId),
        productId: Number(formData.productId),
        productText: formData.productName,
        value: detail.value || "Y",
        userId: Number(userId),
        fileName: detail.s3ActualPath || "",
        auditDate: auditDate || "",
        s3pathActualFile: detail.s3ActualPath || "",
        s3pathAnnotatedFile: detail.s3ActualPath || "",
      });
    
      toast({ title: "Updated successfully", status: "success", duration: 2000, isClosable: true });
      onUpdateClose();
      onRefresh?.(); 
    } catch {
      toast({ title: "Update failed", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsUpdating(false);
    }
  };

  // ── DELETE 
  const handleDeleteClick = (detail, idx) => {

    toast({
      title: "Confirm Delete",
      description: `Delete "${detail.product?.productName || "this product"}"?`,
      status: "warning",
      duration: null,       
      isClosable: true,
      position: "top",
      render: ({ onClose }) => (
        <Box bg="white" border="1px solid" borderColor="orange.300" borderRadius="md" p={4} boxShadow="lg">
          <Flex direction="column" gap={2}>
            <Box fontWeight="bold" color="orange.700">Confirm Delete</Box>
            <Box color="gray.600" fontSize="sm">
              Delete &quot;{detail.product?.productName || "this product"}&quot;? This cannot be undone.
            </Box>
            <Flex gap={2} mt={1}>
              <Button
                size="sm"
                colorScheme="red"
                onClick={() => {
                  onClose();
                  executeDelete(detail, idx);
                }}
              >
                Yes, Delete
              </Button>
              <Button size="sm" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </Flex>
          </Flex>
        </Box>
      ),
    });
  };

  const executeDelete = async (detail, idx) => {
    setDeletingIdx(idx);
    try {
      const runId = scoreBreakdown?.availability?.products?.[0]?.runId;
      const storeId = scoreBreakdown?.store?.storeId;
      const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
      const auditDate = selectedStoreDetails?.auditedDate;
  

      await apiClient.delete(API_CONFIG.ENDPOINTS.STORE_REPORT.ACTIVATION.DELETE, {
        data: {                             
          runId: Number(runId),
          storeId: Number(storeId),
          productId: Number(detail.product?.productId),
          productText: detail.product?.productName || "",
          value: detail.value || "Y",
          userId: Number(userId),
          fileName: detail.s3ActualPath || "",     
          auditDate: auditDate || "",
          s3pathActualFile: detail.s3ActualPath || "",
          s3pathAnnotatedFile: detail.s3ActualPath || "",
        },
      });


     toast({
        title: "Deleted successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onRefresh?.(); 
    } catch {
      toast({
        title: "Delete failed",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeletingIdx(null);
    }
  };
 
  return (
    <Box
      bg="white" borderRadius="xl" border="1px solid"
      borderColor="gray.200" boxShadow="lg" p={6}
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center">
          <Icon as={FiCheckCircle} color="yellow.600" boxSize={6} mr={3} />
          <Heading size="md" color="gray.800" fontWeight="semibold">
            Activation Details
          </Heading>
        </Flex>
        <Badge colorScheme="blue" fontSize="md" variant="solid" px={4} py={2}>
          activationScore:{" "}
          {activation?.activationScore
            ? `${activation.activationScore} out of ${activation?.maxActivationScore ?? ""}`
            : `0 out of ${activation?.maxActivationScore ?? ""}`
          }
        </Badge>
      </Flex>

      {activation?.activationDetails?.length > 0 ? (
        <>
          <TableContainer>
            <Table variant="simple">
              <Thead bg="yellow.50">
                <Tr>
                  <Th color="yellow.700" fontWeight="bold">Company</Th>
                  <Th color="yellow.700" fontWeight="bold">Brand</Th>
                  <Th color="yellow.700" fontWeight="bold">Product</Th>
                  <Th color="yellow.700" fontWeight="bold">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {activation.activationDetails.map((detail, idx) => (
                  <Tr key={idx} bg={idx % 2 === 0 ? "yellow.50" : "transparent"}>
                    <Td fontWeight="medium">
                      {detail.product?.productCompany?.productCompanyName || "-"}
                    </Td>
                    <Td>{detail.product?.productBrand?.productBrandName || "-"}</Td>
                    <Td>{detail.product?.productName || "-"}</Td>
                    <Td>
                      <Flex gap={4} justify="space-between">
                        <Button
                          size="sm"
                          colorScheme="yellow"
                          onClick={() => onViewActivation(detail)}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<FiEdit2 />}
                          onClick={() => handleUpdateClick(detail)}
                        >
                          Update
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          leftIcon={<FiTrash2 />}
                          isLoading={deletingIdx === idx}
                          loadingText="Deleting..."
                          onClick={() => handleDeleteClick(detail, idx)}
                        >
                          Delete
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Center h="200px" color="gray.500">No activation details available</Center>
      )}
      
          <Flex justify="flex-end" mt={6} gap={4}>
            <Button
              leftIcon={<FiEdit2 />}
              colorScheme="red"
              variant="solid"
              size="md"
              onClick={onEditOpen}
              _hover={{ bg: "red.600", transform: "translateY(-2px)", boxShadow: "md" }}
              transition="all 0.2s"
            >
              Edit
            </Button>
            <Button
              leftIcon={<FiCheckCircle />}
              colorScheme="green"
              variant="solid"
              size="md"
              onClick={onValidateAct}
              isLoading={isValidatingAct}
              loadingText="Validating..."
              _hover={{ bg: "green.600", transform: "translateY(-2px)", boxShadow: "md" }}
              transition="all 0.2s"
            >
              Validate Activation
            </Button>
          </Flex>

      <UpdateActivationModal
        isOpen={isUpdateOpen}
        onClose={onUpdateClose}
        detail={selectedDetail}
        isLoadingImage={isLoadingActivationImage}
        onSave={handleUpdateSave}
        isUpdating={isUpdating}
      />
      <EditActivationModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        selectedStoreDetails={selectedStoreDetails}
        generatePresignedUrls={generatePresignedUrls}
        scoreBreakdown={scoreBreakdown}
        onRefresh={onRefresh}
      />
    </Box>
  );
}
